// VAPID configuration
export const VAPID_CONFIG = {
  // These should be environment variables in production
  publicKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || '',
  // Private key should be on your server only
};

// Convert VAPID key from base64 to Uint8Array
export function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as BufferSource;
}

// Check if push notifications are supported
export function isPushNotificationSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// Register service worker and get push subscription
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) {
    console.warn('Push notifications are not supported in this browser');
    return null;
  }

  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw.js');
    //console.log('Service Worker registered:', registration);

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription && VAPID_CONFIG.publicKey) {
      // Subscribe to push notifications
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_CONFIG.publicKey)
      });
    }

    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
}

// Send subscription to your server
export async function sendSubscriptionToServer(subscription: PushSubscription): Promise<boolean> {
  try {
    const response = await fetch('/api/push-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscription),
    });

    const result = await response.json();
    
    if (response.ok) {
      //console.log('Subscription sent to server successfully:', result.message);
      return true;
    } else {
      console.error('Failed to send subscription to server:', result.error);
      return false;
    }
  } catch (error) {
    console.error('Error sending subscription to server:', error);
    return false;
  }
}

// Remove subscription from server
export async function removeSubscriptionFromServer(subscription: PushSubscription): Promise<boolean> {
  try {
    const response = await fetch('/api/push-subscription', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error removing subscription from server:', error);
    return false;
  }
}

// Test function to send a test notification
export async function sendTestNotification(subscription: PushSubscription): Promise<boolean> {
  try {
    const response = await fetch('/api/send-push', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription,
        payload: {
          title: 'Test Notification 🔔',
          body: 'This is a test notification to verify push notifications are working!',
          tag: 'test',
          data: {
            type: 'test',
            timestamp: Date.now()
          }
        }
      }),
    });

    const result = await response.json();
    return response.ok && result.success;
  } catch (error) {
    console.error('Error sending test notification:', error);
    return false;
  }
}
